import {
  Directive,
  Input,
  Attribute,
  Inject,
  ElementRef,
  Renderer2
} from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { BaseNumberInputDirective } from './base-number-input.directive';
import { NumberInputService } from './number-input.service';

import type {
  OnChanges,
  SimpleChanges
} from '@angular/core';
import type {
  ControlValueAccessor
} from '@angular/forms';

import type { FormatParameters } from './input.domain';

/**
 * Number Input Directive
 *
 * Allows an input type number to be formatted on blur
 *
 * @example
 * <input type="number" tdWbNumberInput="n4-2">
 */
@Directive({
  selector: 'input[tdWbNumberInput]',
  providers: [ValueAccessorProviderFactory(NumberInputDirective)]
})
export class NumberInputDirective extends BaseNumberInputDirective implements ControlValueAccessor, OnChanges {
  private static readonly DEFAULT_FORMAT: string = 'n0';

  @Input('tdWbNumberInput') format: string;

  constructor(@Attribute('tdWbNumberInputGroupSeparator') groupSeparator: 'true' | 'false' | null,
              @Inject(ElementRef) protected elementRef: ElementRef,
              @Inject(Renderer2) protected renderer: Renderer2,
              @Inject(NumberInputService) protected numberInputFormatService: NumberInputService) {
    super(groupSeparator, elementRef, renderer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.format !== undefined) {
      if (changes.format.currentValue === undefined || changes.format.currentValue === '') {
        this.format = NumberInputDirective.DEFAULT_FORMAT;
      }
      super.onChanges();
    }
  }

  protected formatValue(value: number): string {
    return this.numberInputFormatService.format(value, this.getFormat());
  }

  protected parseValue(value: string): number {
    const { format, decimalTruncate }: { format?: string, decimalTruncate?: boolean } = this.getFormat();
    return this.numberInputFormatService.parse(value, format, decimalTruncate);
  }

  protected getFormat(): FormatParameters {
    let format = this.format;
    let decimalTruncate = false;
    const noGroupSeparator = this.focused || !this.groupSeparator;
    const editFormat = this.focused;
    if (this.numberInputFormatService.isCurrencyFormat(format)) {
      if (this.focused) {
        format = `n${format.substring(1)}`; // switch to numeric format when focused - no currency symbol.
      }
    } else if (this.numberInputFormatService.isDecimalTruncate(format)) {
      decimalTruncate = true;
      format = `n${format.substring(1)}`;
    }
    return { format, noGroupSeparator, editFormat, decimalTruncate };
  }
}
